import {Button, FormField, Form, AutoSave} from 'platform/components';
import {Box, Grid, Heading, HStack, VStack, Show} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import i18n from '@dms/i18n';

import {getApiDateString, parseDate} from 'shared/web';

import {useInterestData} from '../../../../hooks/useInterestData';
import {ExternalFilter} from '../../types/ExternalFilter';

interface TestDriveFormProps {
  interestId: string;
  onMinipurchaseUpdate: (values: ExternalFilter) => void;
  hideScheduleButton?: boolean;
  isDisabled?: boolean;
}

const REQUESTS_TEST_DRIVE_OPTIONS = [
  {
    label: i18n.t('general.labels.yes'),
    value: 'yes',
  },
  {
    label: i18n.t('general.labels.no'),
    value: 'no',
  },
];

export function TestDriveForm(props: TestDriveFormProps) {
  const formatTime = useDateTimeFormatter();
  const {miniPurchase} = useInterestData(props.interestId);

  return (
    <>
      <HStack width="100%" justify="space-between" align="flex-end">
        <Heading size={4}>{i18n.t('entity.interest.selling.testDrive')}</Heading>
        <Show when={!props.hideScheduleButton}>
          <Button
            size="small"
            variant="link"
            title={i18n.t('entity.interest.selling.scheduleTestDrive')}
            leftIcon="action/calendar_today"
            onClick={() => {}}
          />
        </Show>
      </HStack>

      <Box>
        <Form
          isDisabled={props.isDisabled}
          defaultValues={{
            testDriveRequested: miniPurchase?.testDriveRequested ? ['yes'] : ['no'],
            testDriveScheduledAtDate: miniPurchase?.testDriveScheduledAt
              ? getApiDateString(parseDate(miniPurchase.testDriveScheduledAt))
              : null,
            testDriveScheduledAtTime: miniPurchase?.testDriveScheduledAt
              ? formatTime('timeShort', parseDate(miniPurchase.testDriveScheduledAt))
              : null,
            testDriveNote: miniPurchase?.testDriveNote,
          }}
        >
          {(control, formApi) => (
            <>
              <AutoSave<ExternalFilter>
                onSave={(values) => {
                  props.onMinipurchaseUpdate(values);
                  // Reset the form dirty baseline to current values after each save.
                  // AutoSave only fires when isDirty is true, which react-hook-form sets to false
                  // when the field value returns to its original defaultValues. Without this reset,
                  // toggling a chips field (e.g. yes → no → yes) would silently skip the second save.
                  formApi.reset(formApi.getValues());
                }}
              />
              <VStack spacing={4}>
                <Grid columns={4}>
                  <FormField
                    label={i18n.t('entity.interest.selling.requestsTestDrive')}
                    name="testDriveRequested"
                    type="chips"
                    options={REQUESTS_TEST_DRIVE_OPTIONS}
                    control={control}
                  />
                  <FormField
                    label={i18n.t('entity.interest.selling.requestedDate')}
                    name="testDriveScheduledAtDate"
                    type="apiDate"
                    control={control}
                  />
                  <FormField
                    label={i18n.t('entity.interest.selling.requestedTime')}
                    name="testDriveScheduledAtTime"
                    type="time"
                    control={control}
                  />

                  <div />
                </Grid>
                <FormField
                  label={i18n.t('entity.interest.selling.testDriveNote')}
                  name="testDriveNote"
                  type="textarea"
                  control={control}
                />
              </VStack>
            </>
          )}
        </Form>
      </Box>
    </>
  );
}
